import {isPassportIdentity} from '@sollapay/domain';
import {SellerType} from '@sollapay/enums';
import {HeaderMetaRow, Typography} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {TrustAccountNumber} from '@/features/trust-accounts/components';

import {TrustAccountSellersHeaderControl} from './TrustAccountSellersHeaderControl';

import type {TrustSellerDetailsDTO} from '@sollapay/types';
import type {HeaderMetaEntry} from '@sollapay/ui/components';
import type {TFunction} from 'i18next';
import type {FC} from 'react';

export interface TrustAccountSellerMetaInfoProps {
  refId: string;
  /** Full seller roster — general-sale accounts can have several, developer sales exactly one. */
  sellers: TrustSellerDetailsDTO[];
  purpose?: string | null;
  /** The deal shape beneath a general sale, already translated. Absent on a developer sale, and on
   * a general-sale account that hasn't answered the transaction-type question yet. */
  transactionType?: string | null;
}

type IdTooltipKind = SellerType | 'passport';

const ID_TOOLTIP_KEYS: Record<IdTooltipKind, string> = {
  [SellerType.CORPORATION]: 'view.header.sellerInfo.tooltips.idNumber.corporation',
  [SellerType.INDIVIDUAL]: 'view.header.sellerInfo.tooltips.idNumber.individual',
  passport: 'view.header.sellerInfo.tooltips.idNumber.passport'
};

export const TrustAccountSellerMetaInfo: FC<TrustAccountSellerMetaInfoProps> = ({
  refId,
  sellers,
  purpose,
  transactionType
}) => {
  const {t} = useTranslation('trustAccounts');

  const purposeValue = purpose && (
    <>
      {purpose}
      {transactionType && (
        <>
          <Typography as="span" size="xs" color="tertiary" className="mx-1">
            ·
          </Typography>
          <Typography as="span" size="xs">
            {transactionType}
          </Typography>
        </>
      )}
    </>
  );

  const numberItem: HeaderMetaEntry = {
    custom: <TrustAccountNumber refId={refId} variant="labeled" />,
    key: 'account-number'
  };
  const purposeItem: HeaderMetaEntry = {
    icon: 'home',
    tooltip: t('view.header.sellerInfo.tooltips.purpose'),
    value: purposeValue
  };

  return <HeaderMetaRow wrap items={[numberItem, purposeItem, ...getSellerItems(sellers, t)]} />;
};

function getSellerItems(sellers: TrustSellerDetailsDTO[], t: TFunction): HeaderMetaEntry[] {
  const primarySeller = sellers[0];
  if (!primarySeller) return [];

  const addressItem: HeaderMetaEntry = {
    icon: 'marker-pin',
    tooltip: t('view.header.sellerInfo.tooltips.address'),
    value: primarySeller.address
  };
  if (sellers.length > 1) {
    return [
      {custom: <TrustAccountSellersHeaderControl sellers={sellers} />, key: 'sellers-control'},
      addressItem
    ];
  }

  const {fullName, idNumber, sellerType, idType} = primarySeller;
  const idTooltipKind: IdTooltipKind | null =
    sellerType === SellerType.INDIVIDUAL && isPassportIdentity(idType) ? 'passport' : sellerType;
  return [
    {icon: 'user-check', tooltip: t('view.header.sellerInfo.tooltips.fullName'), value: fullName},
    {
      icon: 'building',
      tooltip: idTooltipKind ? t(ID_TOOLTIP_KEYS[idTooltipKind]) : '',
      value: idNumber
    },
    addressItem
  ];
}
